import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Gauge } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MarketingPageShell } from './components/MarketingPageShell';
import PostingInsightsPage from './PostingInsightsPage';
import InboxAnalyticsPage from './InboxAnalyticsPage';
import TrackedListeningInsightsPage from './TrackedListeningInsightsPage';
import TrendsPage from './TrendsPage';
import KpisPage from './KpisPage';
import SentimentAnalysisPage from './SentimentAnalysisPage';
import TrafficSourcesPage from './TrafficSourcesPage';

const TABS = [
  { id: 'insights', label: 'Owned' },
  { id: 'inbox', label: 'Inbox' },
  { id: 'tracked', label: 'Tracked' },
  { id: 'trends', label: 'Trends' },
  { id: 'kpis', label: 'KPIs' },
  { id: 'sentiment', label: 'Sentiment' },
  { id: 'sources', label: 'Sources' },
] as const;

type TabId = (typeof TABS)[number]['id'];

function parseTab(value: string | null): TabId {
  if (value === 'inbox' || value === 'kpis' || value === 'sentiment' || value === 'trends' || value === 'insights' || value === 'tracked' || value === 'sources')
    return value;
  return 'insights';
}

const PerformancePage = () => {
  const [params, setParams] = useSearchParams();
  const tab = useMemo(() => parseTab(params.get('tab')), [params]);

  const setTab = (next: TabId) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'insights') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <MarketingPageShell
      title="Performance"
      description="Owned posting analytics from connected accounts, plus listen-target coverage we capture without connecting them."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Radar', href: '/admin/marketing/radar' },
        { label: 'Performance' },
      ]}
      icon={Gauge}
    >
      <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-white p-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tab === t.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'insights' && <PostingInsightsPage embedded />}
      {tab === 'inbox' && <InboxAnalyticsPage />}
      {tab === 'tracked' && <TrackedListeningInsightsPage />}
      {tab === 'trends' && <TrendsPage embedded />}
      {tab === 'kpis' && <KpisPage embedded />}
      {tab === 'sentiment' && <SentimentAnalysisPage embedded />}
      {tab === 'sources' && <TrafficSourcesPage />}
    </MarketingPageShell>
  );
};

export default PerformancePage;
